import { deepClone, formatHumanName } from "@medplum/core";
import type {
  ActivityDefinition,
  Appointment,
  Device,
  Location,
  Patient,
  PlanDefinition,
  Reference,
} from "@medplum/fhirtypes";
import {
  CLINICAL_NOTE_ENTITY_ID_SYSTEM,
  catalogModelKindOf,
  DIAGNOSTIC_DEFINITION_KIND,
  getTRPCClient,
  PATIENT_ENTITY_ID_SYSTEM,
  parseEncounterClassExtension,
  TREATMENT_DEFINITION_KIND,
  VISIT_ENTITY_ID_SYSTEM,
  VISIT_EXECUTION_MEMBER_ID_SYSTEM,
  VISIT_KIND_SYSTEM,
  VISIT_NOTE_CODE,
} from "@repo/api";
import {
  type DraftVisitNoteInput,
  draftVisitNoteInputSchema,
  type UpdateVisitNoteInput,
  updateVisitNoteInputSchema,
  type VisitIdInput,
  visitIdInputSchema,
} from "@repo/api/schemas";
import {
  AD_HOC_PROTOCOL_TITLE,
  CONSULTATION_TYPE_SYSTEM,
  DEFAULT_VISIT_EXECUTION_MEMBER_ID,
  NOTE_CATEGORIES,
  Patient as PatientModel,
  type VisitId,
  type VisitStatus,
} from "@repo/domain";
import { ACT_CODE_SYSTEM, extensionUrl } from "@repo/fhir-systems";
import { coalesce, eq, inArray, useLiveQuery } from "@tanstack/react-db";
import { useMemo } from "react";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import { DomainResultError } from "../core/write-errors";
import type {
  AppointmentRow,
  ClinicalImpressionRow,
  EncounterRow,
  RequestGroupRow,
  ScheduleRow,
  SlotRow,
} from "../fhir/types";
import { patientDomainId } from "./_patient-reference";
import { optimisticVisitExecutionReference } from "./_visit-reference";
import { categoryExtensions } from "./note";
import { protocolDisplayTitle } from "./shared/protocol-display-title";
import type { CodeableConcept } from "./shared/types";

const APPOINTMENT_COLLECTION = "appointment";
const ENCOUNTER_COLLECTION = "encounter";
const PLAN_DEFINITION_COLLECTION = "planDefinition";
const REQUEST_GROUP_COLLECTION = "requestGroup";
const BOOKED_SLOT_COLLECTION = "booked-slot";
const SCHEDULE_COLLECTION = "schedule";
const LOCATION_COLLECTION = "location";
const DEVICE_COLLECTION = "device";
const PATIENT_COLLECTION = "patient";
const PRACTITIONER_COLLECTION = "practitioner";
const ACTIVITY_DEFINITION_COLLECTION = "activityDefinition";

// ── DTO ─────────────────────────────────────────────────────────

type EncounterStatus =
  | "planned"
  | "arrived"
  | "triaged"
  | "in-progress"
  | "onleave"
  | "finished"
  | "cancelled"
  | "entered-in-error"
  | "unknown";

/**
 * Visit — composite read-model (Appointment + Encounter + PlanDefinition + Slot/Schedule).
 * Normalized, flat, no raw FHIR types.
 */
export interface VisitDto {
  visitId: VisitId;
  /** Order Set this visit executes; absent for a consultation. */
  orderSetId?: string;
  encounterId?: string;
  patientId?: string;
  patientName?: string;
  practitionerId?: string;
  practitionerName?: string;
  episodeOfCareId?: string;
  /**
   * How this visit's work came to be. Stored on the Appointment rather than
   * inferred, which is what makes `consultation` distinguishable at all: it used
   * to be read as "has a consultation type code", and the code is gone.
   */
  kind?: VisitKind;
  /** Canonical URL of the Protocol Definition lineage executed by this visit. */
  protocolDefinitionUrl?: string;
  protocolTitle?: string;
  scheduleId?: string;

  status: VisitStatus;
  encounterStatus?: EncounterStatus;

  /** Encounter class stamped at booking time (AMB/VR) — drives virtual-visit-only UI (e.g. join action). */
  encounterClass?: "AMB" | "VR";

  start?: string;
  end?: string;

  description?: string;
  serviceType?: CodeableConcept;
  /** Legacy consultation coding retained for display when serviceType text is absent. */
  consultationTypeCode?: CodeableConcept;

  /** Trimmed protocol occurrence list so visit-detail can render and link the protocol hover card. */
  protocolActions?: VisitProtocolAction[];

  /** Keys of the Slots backing the appointment, in assignment order. */
  slotKeys?: string[];
  /** Ordered Slot list backing the appointment (one slot per resource × segment). Populated by `useVisit`. */
  slots?: VisitSlot[];
  /** Slots grouped by start/end into segments, with resources resolved from each slot's Schedule actor. Populated by `useVisit`. */
  segments?: VisitSegment[];
}

export interface VisitSlot {
  /** The Slot's key. */
  id: string;
  start?: string;
  end?: string;
  scheduleId?: string;
  actorType?: "Practitioner" | "Location" | "Device" | "Other";
  actorId?: string;
  actorName?: string;
}

export interface VisitSegment {
  start?: string;
  end?: string;
  slotIds: string[];
  locationId?: string;
  locationName?: string;
  deviceId?: string;
  deviceName?: string;
}

export interface VisitProtocolAction {
  title: string;
  description?: string;
  definitionUrl?: string;
  /** Catalog type of the referenced ActivityDefinition — used by UI to route to the right detail page. */
  activityType?: "treatment" | "diagnostic";
}

// ── Projection ──────────────────────────────────────────────────

/**
 * TOTAL over the ladder, deliberately: add or rename a rung in `@repo/domain`
 * and this fails to compile until it is given an Appointment spelling here.
 * The read model used to retype the vocabulary, so the same edit compiled clean
 * and broke in the browser instead.
 */
const VISIT_TO_APPOINTMENT_STATUS: Record<VisitStatus, string> = {
  proposed: "proposed",
  booked: "booked",
  // FHIR's Appointment `arrived` is the clinician having started, not the
  // patient being in the lobby — that rung is `checked-in`.
  checkedIn: "checked-in",
  inProgress: "arrived",
  completed: "fulfilled",
  cancelled: "cancelled",
  noShow: "noshow",
};

/** Inverted, never written out twice — the halves cannot drift apart. */
const FHIR_TO_VISIT_STATUS: Record<string, VisitStatus | undefined> = {
  ...(Object.fromEntries(
    Object.entries(VISIT_TO_APPOINTMENT_STATUS).map(([visit, appointment]) => [appointment, visit]),
  ) as Record<string, VisitStatus>),
  // Legacy tolerance: Medplum writes `pending` where we mean `proposed`.
  pending: "proposed",
};

function resolvePatient(apt: Appointment, patient: Patient | undefined): { id?: string; name?: string } {
  const part = apt.participant?.find(
    (participant) => participant.actor?.identifier?.system === PATIENT_ENTITY_ID_SYSTEM,
  );
  const id = patientDomainId(part?.actor);
  return { id, name: part?.actor?.display ?? formatPatientName(patient) };
}

function formatPatientName(patient: Patient | undefined): string | undefined {
  const n = PatientModel.legalName(patient?.name);
  if (!n) return undefined;
  return formatHumanName(n) || undefined;
}

interface VisitPractitioner {
  id?: string;
  name?: string;
}

function resolvePractitioner(
  appointment: AppointmentRow,
  practitionersBySlot: Map<string, VisitPractitioner>,
): VisitPractitioner {
  for (const slotKey of appointment.slotKeys ?? []) {
    const practitioner = practitionersBySlot.get(slotKey);
    if (practitioner) return practitioner;
  }
  return {};
}

function resolveEpisodeOfCareId(apt: AppointmentRow, enc: EncounterRow | undefined): string | undefined {
  return enc?.episodeOfCareId ?? apt.episodeOfCareId;
}

const VISIT_KINDS = ["consultation", "standalone", "curated-session"] as const;
export type VisitKind = (typeof VISIT_KINDS)[number];

function resolveVisitKind(apt: Appointment): VisitKind | undefined {
  const code = apt.appointmentType?.coding?.find((c) => c.system === VISIT_KIND_SYSTEM)?.code;
  return VISIT_KINDS.find((known) => known === code);
}

function normalizeServiceType(apt: Appointment): CodeableConcept | undefined {
  const st = apt.serviceType?.[0];
  if (!st) return undefined;
  const first = st.coding?.[0];
  const result: CodeableConcept = {};
  if (first?.system) result.system = first.system;
  if (first?.code) result.code = first.code;
  if (first?.display) result.display = first.display;
  if (st.text) result.text = st.text;
  return result;
}

function resolveConsultationTypeCode(apt: Appointment): CodeableConcept | undefined {
  const concept = apt.appointmentType;
  const coding = concept?.coding?.find((candidate) => candidate.system === CONSULTATION_TYPE_SYSTEM);
  if (!coding?.code) return undefined;
  return {
    system: coding.system,
    code: coding.code,
    ...(coding.display ? { display: coding.display } : {}),
    ...(concept?.text ? { text: concept.text } : {}),
  };
}

function resolveEncounterClass(apt: Appointment): "AMB" | "VR" | undefined {
  const parsed = parseEncounterClassExtension(apt.extension);
  return parsed?.code === "VR" ? "VR" : parsed ? "AMB" : undefined;
}

interface VisitRowInput {
  appointment: AppointmentRow;
  encounter: EncounterRow | undefined;
  orderSet?: RequestGroupRow;
  planDefinitionsByCanonical?: Map<string, PlanDefinition>;
  slot: SlotRow | undefined;
  schedule?: ScheduleRow;
  patient?: Patient | undefined;
  practitionersBySlot?: Map<string, VisitPractitioner>;
  activityDefinitionsByCanonical?: Map<string, ActivityDefinition>;
}

const EMPTY_AD_MAP: Map<string, ActivityDefinition> = new Map();
const EMPTY_PD_MAP: Map<string, PlanDefinition> = new Map();
const EMPTY_PRACTITIONER_MAP: Map<string, VisitPractitioner> = new Map();

export function projectVisitRow({
  appointment,
  encounter,
  orderSet,
  planDefinitionsByCanonical = EMPTY_PD_MAP,
  slot,
  schedule,
  patient: patientResource,
  practitionersBySlot = EMPTY_PRACTITIONER_MAP,
  activityDefinitionsByCanonical = EMPTY_AD_MAP,
}: VisitRowInput): VisitDto | undefined {
  const visitId = visitIdInputSchema.shape.visitId.safeParse(appointment.domainId);
  if (!visitId.success) return undefined;
  const planDefinition = orderSet?.protocolCanonical
    ? planDefinitionsByCanonical.get(orderSet.protocolCanonical)
    : undefined;
  const patient = resolvePatient(appointment, patientResource);
  const practitioner = resolvePractitioner(appointment, practitionersBySlot);
  const encounterClass = resolveEncounterClass(appointment);
  const storedStatus = FHIR_TO_VISIT_STATUS[appointment.status ?? ""] ?? "booked";
  // Earlier in-person check-ins stopped at the virtual waiting state.
  const status = encounterClass === "AMB" && storedStatus === "checkedIn" ? "inProgress" : storedStatus;
  return {
    visitId: visitId.data,
    orderSetId: orderSet?.domainId,
    encounterId: encounter?.id,
    patientId: patient.id,
    patientName: patient.name,
    practitionerId: practitioner.id,
    practitionerName: practitioner.name,
    episodeOfCareId: resolveEpisodeOfCareId(appointment, encounter),
    kind: resolveVisitKind(appointment),
    protocolDefinitionUrl: planDefinition?.url,
    protocolTitle:
      orderSet && !orderSet.protocolCanonical
        ? AD_HOC_PROTOCOL_TITLE
        : planDefinition && protocolDisplayTitle(planDefinition),
    protocolActions: projectProtocolActions(planDefinition, activityDefinitionsByCanonical),
    scheduleId: slot?.scheduleDomainId ?? schedule?.domainId,

    status,
    encounterStatus:
      status === "inProgress" && encounter?.status === "arrived"
        ? "in-progress"
        : (encounter?.status as EncounterStatus | undefined),
    encounterClass,

    start: appointment.start,
    end: appointment.end,

    description: appointment.description,
    serviceType: normalizeServiceType(appointment),
    consultationTypeCode: resolveConsultationTypeCode(appointment),

    slotKeys: appointment.slotKeys,
  };
}

// ── Hooks ───────────────────────────────────────────────────────

interface UseVisitsParams {
  /** Filter to a single patient. */
  patientId?: string;
  /** Filter to visits scheduled on a single schedule. */
  scheduleId?: string;
  /** Filter to visits scheduled on any of these schedules (overlay). */
  scheduleIds?: string[];
  /** Include only visits with one of these statuses. */
  status?: VisitStatus[];
  /** Exclude visits with any of these statuses. */
  excludeStatus?: VisitStatus[];
}

interface UseVisitsResult {
  visits: VisitDto[];
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
}

/**
 * List visits joined with Encounter + PlanDefinition + Slot.
 * Patient and schedule filters push into the query builder; status filters
 * apply to the projected domain status.
 */
export function useVisits(params?: UseVisitsParams): UseVisitsResult {
  const appointmentCollection = useCollection(APPOINTMENT_COLLECTION);
  const encounterCollection = useCollection(ENCOUNTER_COLLECTION);
  const requestGroupCollection = useCollection(REQUEST_GROUP_COLLECTION);
  const protocolsByCanonical = usePlanDefinitionsByCanonical();
  const slotCollection = useCollection(BOOKED_SLOT_COLLECTION);
  const scheduleCollection = useCollection(SCHEDULE_COLLECTION);
  const patientCollection = useCollection(PATIENT_COLLECTION);
  const practitionersBySlot = usePractitionersBySlot();
  const definitionsByCanonical = useActivityDefinitionsByCanonical();

  const patientId = params?.patientId;
  const scheduleId = params?.scheduleId;
  const scheduleIds = params?.scheduleIds;
  const statusFilter = params?.status;
  const excludeStatus = params?.excludeStatus;

  // On the projected DOMAIN status, not `appointment.status`: the two vocabularies
  // differ (`inProgress` is stored `arrived`), and an in-person `checked-in` projects
  // to `inProgress`, so no Appointment-side spelling filters correctly.
  const keepStatus = (dto: VisitDto | undefined): VisitDto | undefined => {
    if (!dto) return undefined;
    if (statusFilter?.length && !statusFilter.includes(dto.status)) return undefined;
    if (excludeStatus?.includes(dto.status)) return undefined;
    return dto;
  };

  const statusKey = statusFilter?.join(",") ?? "";
  const excludeKey = excludeStatus?.join(",") ?? "";
  const scheduleIdsKey = scheduleIds?.join(",") ?? "";
  const protocolProjectionKey = [...protocolsByCanonical.entries()];
  const activityProjectionKey = [...definitionsByCanonical.entries()];
  const practitionerProjectionKey = [...practitionersBySlot.entries()];

  const result = useLiveQuery({
    queryKey: [
      appointmentCollection.id,
      encounterCollection.id,
      requestGroupCollection.id,
      slotCollection.id,
      scheduleCollection.id,
      patientCollection.id,
      "visits",
      patientId ?? null,
      statusKey,
      excludeKey,
      scheduleId ?? null,
      scheduleIdsKey,
      protocolProjectionKey,
      activityProjectionKey,
      practitionerProjectionKey,
    ],
    query: (q) => {
      const base = q
        .from({ appointment: appointmentCollection })
        .join(
          { encounter: encounterCollection },
          // `visitJoinKey`, not `visitId`: BOTH sides of this key became optional when the
          // join moved onto domain identity, and a left join on two undefined keys matches
          // every row whose key is also undefined — the same fanout the protocol-hop comment
          // below describes. The sentinel keeps the Encounter side always defined, so an
          // Appointment without the visit identifier matches nothing instead of everything.
          ({ appointment, encounter }) => eq(encounter.visitJoinKey, appointment.domainId),
          "left",
        )
        // TWO HOPS TO THE PROTOCOL, because the Appointment no longer names it:
        // the visit points at its order set, and the SET remembers which catalog
        // protocol it was realized from.
        //
        // Only the first hop is a join. The second is a lookup, and deliberately
        // — it keys on a canonical `url`, which a consultation has none of, and a
        // left join on an undefined key matches every row whose key is ALSO
        // undefined. That fanned every consult out into one copy per url-less
        // PlanDefinition, five of them in a seeded database, rendering as five
        // identical appointments on the calendar.
        .join(
          { orderSet: requestGroupCollection },
          ({ appointment, orderSet }) => eq(orderSet.domainId, appointment.orderSetJoinKey),
          "left",
        )
        .join(
          { slot: slotCollection },
          ({ appointment, slot }) => eq(slot.slotKey, appointment.firstSlotJoinKey),
          "left",
        )
        .join(
          { schedule: scheduleCollection },
          ({ slot, schedule }) => eq(schedule.domainId, slot?.scheduleJoinKey),
          "left",
        )
        .join(
          { patient: patientCollection },
          ({ appointment, patient }) => eq(patient.domainId, appointment.patientId),
          "left",
        )
        .orderBy(({ appointment }) => appointment.start, "asc");

      const withPatient = patientId ? base.where(({ appointment }) => eq(appointment.patientId, patientId)) : base;
      const withSchedule = scheduleId
        ? withPatient.where(({ schedule }) => eq(schedule.domainId, scheduleId))
        : scheduleIds?.length
          ? withPatient.where(({ schedule }) => inArray(schedule.domainId, scheduleIds))
          : withPatient;

      return withSchedule.fn.select((row) => ({
        dto: keepStatus(
          projectVisitRow({
            appointment: row.appointment as AppointmentRow,
            encounter: row.encounter as EncounterRow | undefined,
            orderSet: row.orderSet as RequestGroupRow | undefined,
            planDefinitionsByCanonical: protocolsByCanonical,
            slot: row.slot as SlotRow | undefined,
            schedule: row.schedule as ScheduleRow | undefined,
            patient: row.patient as Patient | undefined,
            practitionersBySlot,
            activityDefinitionsByCanonical: definitionsByCanonical,
          }),
        ),
      }));
    },
  });

  return {
    visits: ((result.data as Array<{ dto?: VisitDto }> | undefined) ?? []).flatMap((row) => (row.dto ? [row.dto] : [])),
    isLoading: result.isLoading,
    isReady: result.isReady,
    isError: result.isError,
  };
}

interface UseVisitResult {
  visit: VisitDto | null;
  isLoading: boolean;
  isReady: boolean;
}

/** Single visit by appointment id. `visit` is `null` until ready or if not found. */
export function useVisit(visitId: string | undefined): UseVisitResult {
  const appointmentCollection = useCollection(APPOINTMENT_COLLECTION);
  const encounterCollection = useCollection(ENCOUNTER_COLLECTION);
  const slotCollection = useCollection(BOOKED_SLOT_COLLECTION);
  const scheduleCollection = useCollection(SCHEDULE_COLLECTION);
  const locationCollection = useCollection(LOCATION_COLLECTION);
  const deviceCollection = useCollection(DEVICE_COLLECTION);
  const patientCollection = useCollection(PATIENT_COLLECTION);
  const practitionersBySlot = usePractitionersBySlot();
  const practitionerProjectionKey = [...practitionersBySlot.entries()];

  const result = useLiveQuery({
    queryKey: [
      appointmentCollection.id,
      encounterCollection.id,
      slotCollection.id,
      patientCollection.id,
      practitionerProjectionKey,
      "visit",
      visitId ?? null,
    ],
    query: (q) => {
      if (!visitId) return undefined;
      return q
        .from({ appointment: appointmentCollection })
        .where(({ appointment }) => eq(appointment.domainId, visitId))
        .join(
          { encounter: encounterCollection },
          ({ appointment, encounter }) => eq(encounter.visitId, appointment.domainId),
          "left",
        )
        .join(
          { slot: slotCollection },
          ({ appointment, slot }) => eq(slot.slotKey, appointment.firstSlotJoinKey),
          "left",
        )
        .join(
          { patient: patientCollection },
          ({ appointment, patient }) => eq(patient.domainId, appointment.patientId),
          "left",
        )
        .fn.select((row) => ({
          dto: projectVisitRow({
            appointment: row.appointment as AppointmentRow,
            encounter: row.encounter as EncounterRow | undefined,
            slot: row.slot as SlotRow | undefined,
            patient: row.patient as Patient | undefined,
            practitionersBySlot,
          }),
        }))
        .findOne();
    },
  });

  const baseVisit = (result.data as { dto?: VisitDto } | undefined)?.dto ?? null;

  const slotKeys = baseVisit?.slotKeys;
  const slotKeysKey = slotKeys?.join(",") ?? "";
  const slotsResult = useLiveQuery({
    queryKey: [
      slotCollection.id,
      scheduleCollection.id,
      locationCollection.id,
      deviceCollection.id,
      "visit-slots",
      slotKeysKey,
    ],
    query: (q) => {
      if (!slotKeys?.length) return undefined;
      return q
        .from({ slot: slotCollection })
        .where(({ slot }) => inArray(slot.slotKey, slotKeys))
        .join(
          { schedule: scheduleCollection },
          ({ slot, schedule }) => eq(schedule.domainId, slot.scheduleJoinKey),
          "left",
        )
        .join(
          { location: locationCollection },
          ({ schedule, location }) => eq(location.domainId, schedule?.locationId),
          "left",
        )
        .join({ device: deviceCollection }, ({ schedule, device }) => eq(device.domainId, schedule?.deviceId), "left")
        .orderBy(({ slot }) => slot.start, "asc")
        .fn.select(
          ({ slot, schedule, location, device }) =>
            ({
              id: slot.slotKey,
              start: slot.start,
              end: slot.end,
              scheduleId: slot.scheduleDomainId ?? schedule?.domainId,
              actorType: schedule?.actorType,
              actorId: schedule?.actorId,
              actorName: resolveSlotActorName(schedule?.actorType, location, device, slot.schedule?.display),
            }) satisfies VisitSlot,
        );
    },
  });
  const slots = (slotsResult.data as VisitSlot[] | undefined) ?? undefined;

  // Same object until its inputs change: the visit page renders from it, and a
  // fresh one per render re-renders every procedure card under it.
  const visit = useMemo(() => {
    if (!baseVisit) return baseVisit;
    const segments = slots?.length ? groupSlotsIntoSegments(slots) : undefined;
    return {
      ...baseVisit,
      ...(slots?.length ? { slots } : {}),
      ...(segments?.length ? { segments } : {}),
    };
  }, [baseVisit, slots]);

  return {
    visit,
    isLoading: result.isLoading,
    isReady: result.isReady,
  };
}

/**
 * Stable Map<canonical, ActivityDefinition> built from the AD collection, used
 * to resolve the nested canonical declarations in redesigned protocols.
 */
function resolveSlotActorName(
  actorType: "Practitioner" | "Location" | "Device" | "Other" | undefined,
  location: Location | undefined,
  device: Device | undefined,
  inlineDisplay: string | undefined,
): string | undefined {
  if (inlineDisplay) return inlineDisplay;
  if (actorType === "Location") return location?.name;
  if (actorType === "Device" && device?.deviceName?.length) {
    const friendly = device.deviceName.find((n) => n.type === "user-friendly-name");
    return friendly?.name ?? device.deviceName[0]?.name;
  }
  return undefined;
}

/**
 * Group location/device slots by start/end into procedure segments. Each segment
 * may have multiple slots (one per resource schedule — location + device) and
 * carries the actor of each slot's Schedule on the matching field.
 *
 * The practitioner slot spans the whole session (the session envelope, surfaced
 * separately as the Provider) and is skipped — otherwise it renders as a phantom
 * full-span "No resources" segment that overlaps every procedure.
 */
export function groupSlotsIntoSegments(slots: VisitSlot[]): VisitSegment[] {
  const byKey = new Map<string, VisitSegment>();
  for (const s of slots) {
    if (s.actorType === "Practitioner") continue;
    const key = `${s.start ?? ""}|${s.end ?? ""}`;
    let seg = byKey.get(key);
    if (!seg) {
      seg = { start: s.start, end: s.end, slotIds: [] };
      byKey.set(key, seg);
    }
    seg.slotIds.push(s.id);
    if (s.actorType === "Location" && s.actorId) {
      seg.locationId = s.actorId;
      if (s.actorName) seg.locationName = s.actorName;
    } else if (s.actorType === "Device" && s.actorId) {
      seg.deviceId = s.actorId;
      if (s.actorName) seg.deviceName = s.actorName;
    }
  }
  return [...byKey.values()].sort((a, b) => new Date(a.start ?? 0).getTime() - new Date(b.start ?? 0).getTime());
}

function useActivityDefinitionsByCanonical(): Map<string, ActivityDefinition> {
  const adCollection = useCollection(ACTIVITY_DEFINITION_COLLECTION);
  const result = useLiveQuery({ query: (q) => q.from({ ad: adCollection }) });
  return useMemo(() => {
    const map = new Map<string, ActivityDefinition>();
    const rows = (result.data as unknown as ActivityDefinition[] | undefined) ?? [];
    for (const ad of rows) {
      if (!ad?.url) continue;
      if (ad.version) map.set(`${ad.url}|${ad.version}`, ad);
    }
    return map;
  }, [result.data]);
}

/** Join the Slot's Schedule to its practitioner before projecting each visit's slot links. */
function usePractitionersBySlot(): Map<string, VisitPractitioner> {
  const slots = useCollection(BOOKED_SLOT_COLLECTION);
  const schedules = useCollection(SCHEDULE_COLLECTION);
  const practitioners = useCollection(PRACTITIONER_COLLECTION);
  const result = useLiveQuery({
    queryKey: [slots.id, schedules.id, practitioners.id, "visit-practitioners"],
    query: (q) =>
      q
        .from({ slot: slots })
        .join({ schedule: schedules }, ({ slot, schedule }) => eq(slot.scheduleJoinKey, schedule.domainId), "inner")
        .where(({ schedule }) => eq(schedule.actorType, "Practitioner"))
        .join(
          { practitioner: practitioners },
          ({ schedule, practitioner }) =>
            eq(coalesce(schedule.practitionerDomainId, "__no_practitioner__"), practitioner.domainId),
          "left",
        )
        .fn.select(({ slot, schedule, practitioner }) => ({
          slotId: slot.slotKey,
          id: schedule.practitionerDomainId,
          name: schedule.actorDisplay ?? (practitioner?.name?.[0] ? formatHumanName(practitioner.name[0]) : undefined),
        })),
  });
  return useMemo(
    () =>
      new Map((result.data ?? []).flatMap(({ slotId, id, name }) => (slotId ? [[slotId, { id, name }] as const] : []))),
    [result.data],
  );
}

/**
 * Catalog protocols by exact immutable revision. An OrderSet pins the revision
 * it materialized, so a visit must never borrow another revision's display.
 */
function usePlanDefinitionsByCanonical(): Map<string, PlanDefinition> {
  const collection = useCollection(PLAN_DEFINITION_COLLECTION);
  const result = useLiveQuery({ query: (q) => q.from({ pd: collection }) });
  return useMemo(() => {
    const map = new Map<string, PlanDefinition>();
    for (const pd of (result.data as unknown as PlanDefinition[] | undefined) ?? []) {
      if (pd?.url && pd.version) map.set(`${pd.url}|${pd.version}`, pd);
    }
    return map;
  }, [result.data]);
}

function projectProtocolActions(
  pd: PlanDefinition | undefined,
  adByCanonical: Map<string, ActivityDefinition>,
): VisitProtocolAction[] | undefined {
  if (!pd?.action?.length) return undefined;
  const actions: VisitProtocolAction[] = [];
  for (const step of pd.action) {
    for (const occurrence of step.action ?? []) {
      const canonical = occurrence.definitionCanonical;
      if (!canonical) continue;
      const ad = adByCanonical.get(canonical);
      const activityType = ad ? resolveActivityType(ad) : undefined;
      const definitionUrl = ad?.url ?? canonical.split("|")[0];
      actions.push({
        title: ad?.title ?? ad?.name ?? step.title ?? "Procedure",
        ...(ad?.description ? { description: ad.description } : {}),
        ...(definitionUrl ? { definitionUrl } : {}),
        ...(activityType ? { activityType } : {}),
      });
    }
  }
  return actions.length ? actions : undefined;
}

function resolveActivityType(ad: ActivityDefinition): "treatment" | "diagnostic" | undefined {
  const kind = catalogModelKindOf(ad);
  if (kind === TREATMENT_DEFINITION_KIND) return "treatment";
  if (kind === DIAGNOSTIC_DEFINITION_KIND) return "diagnostic";
  return undefined;
}

// ── Mutations ───────────────────────────────────────────────────

const appointments = () => getDataStoreClient().getCollection<AppointmentRow, string>(APPOINTMENT_COLLECTION);
const encounters = () => getDataStoreClient().getCollection<EncounterRow, string>(ENCOUNTER_COLLECTION);

/** The visit's execution Encounter row key, found by the visit identity it carries. */
function encounterKeyForVisit(visitId: string): string | undefined {
  for (const enc of encounters().values()) {
    if ((enc as EncounterRow).visitId === visitId) return encounters().getKeyFromItem(enc);
  }
  return undefined;
}

function appointmentForVisit(visitId: string): AppointmentRow | undefined {
  return appointments().get(visitId);
}

/** A visit's Appointment row is keyed by the visit id; this only asserts it is loaded. */
function loadedVisit(visitId: string): string {
  if (!appointments().has(visitId)) throw new Error(`Visit '${visitId}' is not loaded`);
  return visitId;
}

/** The visit's OrderSet row key, when the set is loaded. */
function loadedOrderSetKey(visitId: string): string | undefined {
  const orderSetId = appointmentForVisit(visitId)?.orderSetId;
  const requestGroups = getDataStoreClient().getCollection<RequestGroupRow, string>(REQUEST_GROUP_COLLECTION);
  return orderSetId && requestGroups.has(orderSetId) ? orderSetId : undefined;
}

function patientReferenceFromAppointment(apt: Appointment | undefined): Reference<Patient> | undefined {
  return apt?.participant?.find((participant) => participant.actor?.identifier?.system === PATIENT_ENTITY_ID_SYSTEM)
    ?.actor as Reference<Patient> | undefined;
}

/**
 * Check-in starts in-person visits. Virtual visits wait for conference admission.
 */
export const checkIn = defineWrite<VisitIdInput>({
  optimistic: (params) => {
    const input = visitIdInputSchema.parse(params);
    const apt = appointmentForVisit(input.visitId);
    const appointmentKey = loadedVisit(input.visitId);
    const isVirtual = apt && resolveEncounterClass(apt) === "VR";
    const encounterStatus = isVirtual ? "arrived" : "in-progress";
    const startedAt = new Date().toISOString();
    appointments().update(appointmentKey, (draft) => {
      draft.status = isVirtual ? "checked-in" : "arrived";
    });
    const client = getDataStoreClient();
    const setId = apt?.orderSetId;
    if (setId && client.hasCollection("serviceRequest")) {
      const orders = client.getCollection("serviceRequest");
      let stamped = false;
      for (const order of orders.values()) {
        if (order.orderSetId !== setId || order.status === "revoked" || order.status === "completed") continue;
        orders.update(orders.getKeyFromItem(order), (draft) => {
          draft.encounter = optimisticVisitExecutionReference(input.visitId);
        });
        stamped = true;
      }
      if (stamped && loadedOrderSetKey(input.visitId)) {
        client.getCollection("requestGroup").update(setId, (draft) => {
          draft.encounter = optimisticVisitExecutionReference(input.visitId);
        });
      }
    }
    const encId = encounterKeyForVisit(input.visitId);
    if (encId) {
      encounters().update(encId, (draft) => {
        draft.status = encounterStatus;
        if (!isVirtual) draft.period = { ...draft.period, start: startedAt };
      });
      return;
    }
    const subject = patientReferenceFromAppointment(apt);
    if (subject) {
      const encounter: EncounterRow = {
        resourceType: "Encounter",
        identifier: [{ system: VISIT_EXECUTION_MEMBER_ID_SYSTEM, value: DEFAULT_VISIT_EXECUTION_MEMBER_ID }],
        visitId: input.visitId,
        visitJoinKey: input.visitId,
        status: encounterStatus,
        class: { system: ACT_CODE_SYSTEM, code: isVirtual ? "VR" : "AMB" },
        ...(!isVirtual ? { period: { start: startedAt } } : {}),
        subject: deepClone(subject),
        appointment: [{ identifier: { system: VISIT_ENTITY_ID_SYSTEM, value: input.visitId } }],
      };
      encounters().insert(encounter);
    }
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().visit.checkIn.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/**
 * Intent: "let the waiting patient in". This is where the visit actually starts,
 * so it is the rung charting unlocks on.
 */
export const admitVisit = defineWrite<VisitIdInput>({
  optimistic: (params) => {
    const input = visitIdInputSchema.parse(params);
    appointments().update(loadedVisit(input.visitId), (draft) => {
      draft.status = "arrived";
    });
    const encId = encounterKeyForVisit(input.visitId);
    if (encId) {
      encounters().update(encId, (draft) => {
        draft.status = "in-progress";
        draft.period = { ...draft.period, start: new Date().toISOString() };
      });
    }
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().visit.admit.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/**
 * Intent: "complete a visit".
 * Optimistically flips the appointment to `fulfilled` and the linked encounter
 * to `finished`, and completes the linked order set (if present locally).
 */
export const completeVisit = defineWrite<VisitIdInput>({
  optimistic: (params) => {
    const input = visitIdInputSchema.parse(params);
    appointments().update(loadedVisit(input.visitId), (draft) => {
      draft.status = "fulfilled";
    });
    const orderSetKey = loadedOrderSetKey(input.visitId);
    if (orderSetKey) {
      getDataStoreClient()
        .getCollection<RequestGroupRow, string>(REQUEST_GROUP_COLLECTION)
        .update(orderSetKey, (draft) => {
          draft.status = "completed";
        });
    }
    const encId = encounterKeyForVisit(input.visitId);
    if (encId) {
      encounters().update(encId, (draft) => {
        draft.status = "finished";
        draft.period = { ...draft.period, end: new Date().toISOString() };
      });
    }
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().visit.complete.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** Intent: "mark a visit as no-show" */
export const markNoShow = defineWrite<VisitIdInput>({
  optimistic: (params) => {
    const input = visitIdInputSchema.parse(params);
    appointments().update(loadedVisit(input.visitId), (draft) => {
      draft.status = "noshow";
    });
    const key = encounterKeyForVisit(input.visitId);
    if (key)
      encounters().update(key, (draft) => {
        draft.status = "cancelled";
      });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().visit.markNoShow.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

const clinicalImpressions = () =>
  getDataStoreClient().getCollection<ClinicalImpressionRow, string>("clinicalImpression");

function loadedNote(noteId: string): string {
  if (!clinicalImpressions().has(noteId)) throw new Error(`Clinical note '${noteId}' is not loaded`);
  return noteId;
}

/** Intent: "draft a visit-level note" */
export const draftVisitNote = defineWrite<DraftVisitNoteInput>({
  optimistic: (params) => {
    const input = draftVisitNoteInputSchema.parse(params);
    const apt = appointmentForVisit(input.visitId);
    const subject = patientReferenceFromAppointment(apt);
    if (!subject) throw new DomainResultError("This visit is not loaded. Refresh and try again.");
    const optimisticNoteId = input.id;
    clinicalImpressions().insert({
      resourceType: "ClinicalImpression",
      identifier: [{ system: CLINICAL_NOTE_ENTITY_ID_SYSTEM, value: optimisticNoteId }],
      domainId: optimisticNoteId,
      visitId: input.visitId,
      codeCode: VISIT_NOTE_CODE.code,
      codeSystem: VISIT_NOTE_CODE.system,
      status: "in-progress",
      subject: deepClone(subject),
      encounter: optimisticVisitExecutionReference(input.visitId),
      date: new Date().toISOString(),
      code: {
        coding: [{ system: VISIT_NOTE_CODE.system, code: VISIT_NOTE_CODE.code, display: VISIT_NOTE_CODE.display }],
      },
      note: [{ text: input.text }],
      description: input.title,
      extension: categoryExtensions(undefined, NOTE_CATEGORIES.progress),
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().visit.draftNote.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** Intent: "update a visit note's narrative" */
export const updateVisitNote = defineWrite<UpdateVisitNoteInput>({
  optimistic: (params) => {
    const input = updateVisitNoteInputSchema.parse(params);
    clinicalImpressions().update(loadedNote(input.noteId), (draft) => {
      draft.note = [{ text: input.text }];
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().visit.updateNote.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** Intent: "cancel a visit" */
export const cancelVisit = defineWrite<VisitIdInput>({
  optimistic: (params) => {
    const input = visitIdInputSchema.parse(params);
    appointments().update(loadedVisit(input.visitId), (draft) => {
      draft.status = "cancelled";
      draft.slot = [];
    });
    const key = encounterKeyForVisit(input.visitId);
    if (key)
      encounters().update(key, (draft) => {
        draft.status = "cancelled";
      });
    const client = getDataStoreClient();
    for (const name of ["slot", "booked-slot"] as const) {
      if (!client.hasCollection(name)) continue;
      const slots = client.getCollection(name);
      for (const slot of slots.values()) {
        const key = slots.getKeyFromItem(slot);
        if (key.startsWith(`${input.visitId}/`)) slots.delete(key);
      }
    }
    const setId = appointmentForVisit(input.visitId)?.orderSetId;
    if (setId && client.hasCollection("serviceRequest")) {
      const orders = client.getCollection("serviceRequest");
      const charted = new Set(
        client.hasCollection("procedure")
          ? [...client.getCollection("procedure").values()]
              .filter((row) => row.orderSetJoinKey === setId)
              .map((row) => row.orderId)
          : [],
      );
      for (const order of orders.values()) {
        if (order.orderSetId !== setId || charted.has(order.orderId) || order.status === "revoked") continue;
        orders.update(orders.getKeyFromItem(order), (draft) => {
          draft.status = "revoked";
          const url = extensionUrl("order-revoked-at");
          draft.extension = [
            ...(draft.extension ?? []).filter((ext) => ext.url !== url),
            { url, valueDateTime: new Date().toISOString() },
          ];
        });
      }
    }
    const orderSetKey = loadedOrderSetKey(input.visitId);
    if (orderSetKey) {
      getDataStoreClient()
        .getCollection<RequestGroupRow, string>(REQUEST_GROUP_COLLECTION)
        .update(orderSetKey, (draft) => {
          draft.status = "revoked";
        });
    }
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().visit.cancel.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/**
 * True when the visit is a consultation — the one kind that carries no orders.
 * Used by schedule UI to render consults visually distinct.
 *
 * Reads the stored kind. It used to read "does this have a consultation type
 * code", which stopped being true of anything when the code was removed, so
 * every consult quietly rendered as a protocol visit.
 */
export function isConsultVisit(visit: Pick<VisitDto, "kind">): boolean {
  return visit.kind === "consultation";
}
